import { useLayoutEffect, useRef, type RefObject } from 'react'
import { css } from 'styled-components'

import { findScrollableAncestor } from '@root/shared/lib/dom/find-scrollable-ancestor'

const SCROLL_LOCK_ATTR = 'data-trade-tables-scroll-locked'

const ENABLE_INSET_PX = 2
const DISABLE_INSET_PX = 12

/**
 * Companion styles for the lock anchor. While locked, inner virtuoso scrollers
 * are frozen so wheel/touch input falls through to the page scroll natively
 * (no JS wheel forwarding, hence no scroll jumps). The stable scrollbar gutter
 * keeps columns from shifting when the scrollbar (dis)appears on unlock.
 */
export const tradeTablesScrollLockCss = css`
  [data-virtuoso-scroller] {
    scrollbar-gutter: stable;
  }

  &[${SCROLL_LOCK_ATTR}='true'] [data-virtuoso-scroller] {
    overflow: hidden !important;
  }
`

/**
 * Table scroll is allowed only once the whole tables area sits inside the
 * scrollport. Hysteresis (different enable/disable insets) prevents flicker at
 * the boundary. Two fallbacks guarantee the table never gets stuck locked:
 * an area taller than the scrollport that fully covers it, and a page that is
 * already scrolled to its maximum.
 */
const computeScrollEnabled = (
  element: HTMLElement,
  scrollport: HTMLElement,
  currentlyEnabled: boolean,
): boolean => {
  const scrollRect = scrollport.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  const coversScrollport =
    elementRect.height > scrollRect.height &&
    elementRect.top <= scrollRect.top &&
    elementRect.bottom >= scrollRect.bottom

  if (coversScrollport) {
    return true
  }

  const atMaxPageScroll =
    scrollport.scrollTop + scrollport.clientHeight >=
    scrollport.scrollHeight - 1

  if (atMaxPageScroll && elementRect.top >= scrollRect.top) {
    return true
  }

  const inset = currentlyEnabled ? -DISABLE_INSET_PX : ENABLE_INSET_PX

  return (
    elementRect.top >= scrollRect.top + inset &&
    elementRect.bottom <= scrollRect.bottom - inset
  )
}

/**
 * Blocks the trade tables' internal scroll until the entire tables area is
 * visible in the page scrollport. The lock is purely CSS-driven: this hook
 * only toggles an attribute on the anchor element, and
 * `tradeTablesScrollLockCss` (included in the anchor's styles) flips the inner
 * scrollers to `overflow: hidden`. The browser then scrolls the page natively.
 *
 * Pass a `resetKey` (e.g. the active tab) to re-evaluate when content swaps.
 */
export const useTradeTablesScrollLock = (
  anchorRef: RefObject<HTMLElement | null>,
  resetKey?: unknown,
): void => {
  const scrollEnabledRef = useRef(false)

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    if (!anchor) {
      return
    }

    const scrollEl = findScrollableAncestor(anchor)
    if (!(scrollEl instanceof HTMLElement)) {
      return
    }

    let frameId = 0

    const applyLock = (locked: boolean) => {
      anchor.setAttribute(SCROLL_LOCK_ATTR, locked ? 'true' : 'false')
    }

    const update = () => {
      const enabled = computeScrollEnabled(
        anchor,
        scrollEl,
        scrollEnabledRef.current,
      )
      if (enabled === scrollEnabledRef.current) {
        return
      }
      scrollEnabledRef.current = enabled
      applyLock(!enabled)
    }

    const scheduleUpdate = () => {
      if (frameId !== 0) {
        return
      }
      frameId = requestAnimationFrame(() => {
        frameId = 0
        update()
      })
    }

    applyLock(!scrollEnabledRef.current)
    update()

    scrollEl.addEventListener('scroll', scheduleUpdate, { passive: true })

    const resizeObserver = new ResizeObserver(scheduleUpdate)
    resizeObserver.observe(anchor)
    resizeObserver.observe(scrollEl)

    return () => {
      if (frameId !== 0) {
        cancelAnimationFrame(frameId)
      }
      scrollEl.removeEventListener('scroll', scheduleUpdate)
      resizeObserver.disconnect()
      anchor.removeAttribute(SCROLL_LOCK_ATTR)
    }
  }, [anchorRef, resetKey])
}
